// lib/footfall/venue.ts
import type { PropertyVenue, PulseExposure, PulseFlow, PulseVenueAnchor, PulseVenueFeature, UnitStatus } from '#types/types';
import { linkBrand } from '#lib/footfall/pulse';

/** The DataAudit procedure a centre's indoor map is stored under. */
export const VENUE_PROCEDURE = 'FATER_VENUE_V1';
/** The landlord's own merchandising plan for the centre, which wins over the public website wherever it says something. */
export const VENUE_PLAN_PROCEDURE = 'FATER_VENUE_PLAN_V1';

const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
const list = (value: unknown): Record<string, unknown>[] => Array.isArray(value) ? value.map(record) : [];
const finite = (value: unknown): number | null => typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Inverse transverse Mercator on WGS84, northern hemisphere (Snyder's series, sub-metre at this scale). */
export function utmToLngLat(easting: number, northing: number, zone: number): [number, number] {
  const a = 6378137, f = 1 / 298.257223563, e2 = f * (2 - f), ep2 = e2 / (1 - e2), k0 = 0.9996;
  const x = easting - 500000;
  const mu = northing / k0 / (a * (1 - e2 / 4 - 3 * e2 ** 2 / 64 - 5 * e2 ** 3 / 256));
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));
  const p1 = mu + (3 * e1 / 2 - 27 * e1 ** 3 / 32) * Math.sin(2 * mu) + (21 * e1 ** 2 / 16 - 55 * e1 ** 4 / 32) * Math.sin(4 * mu) + (151 * e1 ** 3 / 96) * Math.sin(6 * mu) + (1097 * e1 ** 4 / 512) * Math.sin(8 * mu);
  const c1 = ep2 * Math.cos(p1) ** 2, t1 = Math.tan(p1) ** 2;
  const n1 = a / Math.sqrt(1 - e2 * Math.sin(p1) ** 2), r1 = a * (1 - e2) / (1 - e2 * Math.sin(p1) ** 2) ** 1.5, d = x / (n1 * k0);
  const lat = p1 - (n1 * Math.tan(p1) / r1) * (d ** 2 / 2 - (5 + 3 * t1 + 10 * c1 - 4 * c1 ** 2 - 9 * ep2) * d ** 4 / 24 + (61 + 90 * t1 + 298 * c1 + 45 * t1 ** 2 - 252 * ep2 - 3 * c1 ** 2) * d ** 6 / 720);
  const lng = (d - (1 + 2 * t1 + c1) * d ** 3 / 6 + (5 - 2 * c1 + 28 * t1 - 3 * c1 ** 2 + 8 * ep2 + 24 * t1 ** 2) * d ** 5 / 120) / Math.cos(p1);
  return [(zone - 1) * 6 - 180 + 3 + lng * 180 / Math.PI, lat * 180 / Math.PI];
}

/** How a landlord's Visioglobe map names its objects: which ids are shops, and what marks an entrance, a station or a car park. */
export interface VisioglobeConventions {
  kindOf: (id: string) => PulseVenueFeature['kind'] | null;
  /** An image (a map icon) that stands for an anchor, and the floor it sits on. */
  imageAnchor: (image: Record<string, unknown>, floorOf: (id: string) => string | null) => { kind: PulseVenueAnchor['kind']; floor: string } | null;
  routingAnchor: (id: string) => PulseVenueAnchor['kind'] | null;
  /** Floors missing from the place list are read from the generic poi list (where Westfield files its doors). */
  readsPoiFloors?: boolean;
  /** Shop outlines under this many square metres are signs or brand panels, never lettable units. */
  minUnitSqm?: number;
}

/** Altarea's maps (Espace Saint Quentin): shops are E0-041, entrances an EntreeDuCentre icon, transport and car parks named routing points. */
export const ALTAREA_CONVENTIONS: VisioglobeConventions = {
  kindOf: id => /^E-?\d+-/.test(id) ? 'unit' : /^B\d+$/.test(id) ? 'building' : /^(S\d+_|directories|Parking)/.test(id) ? 'amenity' : null,
  imageAnchor: image => {
    const floor = /EntreeDuCentre/.test(String(image.imageName)) ? /B1-[UL]L\d/.exec(String(image.poiID))?.[0] : undefined;
    return floor ? { kind: 'entrance', floor } : null;
  },
  routingAnchor: id => /garesncf/i.test(id) ? 'station' : /gareroutiere/i.test(id) ? 'bus' : /^(S\d+_parking\d|S\d+_Parking_|Parking)/.test(id) && !/moto/i.test(id) ? 'parking' : null,
};

/** URW's Westfield maps (Rosny 2): a shop is its lease unit number (159b, K10002, EXT3), a centre door gate006, a car park an outdoor parking icon. */
export const WESTFIELD_CONVENTIONS: VisioglobeConventions = {
  kindOf: id => /^B\d+$/.test(id) ? 'building' : !id || id === 'outside' || /^zone-/.test(id) ? null : 'unit',
  imageAnchor: (image, floorOf) => {
    const floor = /^parking(cover)?$/.test(String(image.imageName)) ? floorOf(String(image.poiID)) : null;
    return floor ? { kind: 'parking', floor } : null;
  },
  routingAnchor: id => /^gate\d+$/.test(id) ? 'entrance' : null,
  readsPoiFloors: true,
  // Rosny 2's 278-1..14 are 2 m2 slivers of a brand panel.
  minUnitSqm: 5,
};

export interface WalkingNetwork { nodes: number; edges: Array<[number, number, number]> }

/** Share above the shortest walk a route may run and still count: corridors are meshes of near-equal lines, and a single
 * shortest line is winner-take-all between them (measured on ESQ: ranks moved 18-25 points with the door radius). */
export const ROUTE_TOLERANCE = 0.1;

function distancesFrom(sources: number[], adjacency: Array<Array<[number, number]>>): number[] {
  const distance = new Array<number>(adjacency.length).fill(Infinity), done = new Array<boolean>(adjacency.length).fill(false);
  for (const s of sources) distance[s] = 0;
  for (;;) {
    let node = -1;
    for (let i = 0; i < adjacency.length; i++) if (!done[i] && distance[i]! < Infinity && (node < 0 || distance[i]! < distance[node]!)) node = i;
    if (node < 0) return distance;
    done[node] = true;
    for (const [next, length] of adjacency[node]!) if (distance[node]! + length < distance[next]!) distance[next] = distance[node]! + length;
  }
}

/**
 * How many anchor-to-shop walks pass each shop: each anchor (all its doors at once) counts once; for every anchor and every other shop, the walk is every route within
 * ROUTE_TOLERANCE of the shortest, and it passes a shop when it touches that shop's door or a point one step from it.
 * A walk to a shop never counts for that shop, so a dead end scores only what passes it on the way elsewhere.
 * Percentile is the mid-rank of that count among all shops, so ties share a value.
 */
export const shopExposure = (network: WalkingNetwork, anchors: number[][], doors: Map<string, number[]>) => walkingLoads(network, anchors, doors).exposure;

/** shopExposure's walks, also counted per directed network edge: the corridor flow the Traffic layer draws. */
export function walkingLoads(network: WalkingNetwork, anchors: number[][], doors: Map<string, number[]>): { exposure: Map<string, PulseExposure>; edgeWalks: number[] } {
  const forward = Array.from({ length: network.nodes }, () => [] as Array<[number, number]>);
  const backward = Array.from({ length: network.nodes }, () => [] as Array<[number, number]>);
  for (const [from, to, length] of network.edges) if (from < network.nodes && to < network.nodes) { forward[from]!.push([to, length]); backward[to]!.push([from, length]); }
  const watchers = Array.from({ length: network.nodes }, () => new Set<string>());
  for (const [shop, nodes] of doors) for (const door of nodes) for (const node of [door, ...forward[door]!.map(([n]) => n)]) watchers[node]!.add(shop);
  const toShop = new Map([...doors].map(([shop, nodes]) => [shop, distancesFrom(nodes, backward)]));
  const routes = new Map([...doors.keys()].map(shop => [shop, 0]));
  const edgeWalks = network.edges.map(() => 0);
  for (const anchor of anchors) {
    if (!anchor.length) continue;
    const fromAnchor = distancesFrom(anchor, forward);
    for (const [target, nodes] of doors) {
      const shortest = Math.min(...nodes.map(n => fromAnchor[n]!));
      if (shortest === Infinity) continue;
      const limit = shortest * (1 + ROUTE_TOLERANCE) + 1e-6, rest = toShop.get(target)!, passed = new Set<string>();
      for (let node = 0; node < network.nodes; node++) if (fromAnchor[node]! + rest[node]! <= limit) for (const shop of watchers[node]!) passed.add(shop);
      network.edges.forEach(([from, to, length], i) => { if (fromAnchor[from]! + length + rest[to]! <= limit) edgeWalks[i]!++; });
      passed.delete(target);
      for (const shop of passed) routes.set(shop, routes.get(shop)! + 1);
    }
  }
  const counts = [...routes.values()];
  return { edgeWalks, exposure: new Map([...routes].map(([shop, count]) => {
    const below = counts.filter(c => c < count).length, equal = counts.filter(c => c === count).length;
    return [shop, { routes: count, percentile: counts.length > 1 ? Math.round((below + (equal - 1) / 2) / (counts.length - 1) * 100) : 100 }];
  })) };
}

/** Folds both directions of each corridor segment into one, pointing the way most walks take it; floor changes and unused segments drop out. */
export function corridorFlows(network: WalkingNetwork, edgeWalks: number[], nodeAt: (node: number) => { level: string; at: [number, number] } | null): PulseFlow[] {
  const pairs = new Map<string, { from: number; to: number; ahead: number; back: number }>();
  network.edges.forEach(([from, to], i) => {
    const key = from < to ? `${from}-${to}` : `${to}-${from}`, pair = pairs.get(key) ?? { from: Math.min(from, to), to: Math.max(from, to), ahead: 0, back: 0 };
    if (from === pair.from) pair.ahead += edgeWalks[i]!; else pair.back += edgeWalks[i]!;
    pairs.set(key, pair);
  });
  return [...pairs.values()].flatMap(({ from, to, ahead, back }) => {
    const a = nodeAt(from), b = nodeAt(to);
    if (!a || !b || a.level !== b.level || !(ahead + back)) return [];
    return [{ level: a.level, path: ahead >= back ? [a.at, b.at] : [b.at, a.at], walks: ahead + back }];
  });
}

/**
 * A Visioglobe venue (its map.json) as georeferenced shops, amenities and building footprints. Pixel y grows
 * downward from svgHeight; the vendor's geoinformation turns (x, svgHeight - y) into UTM metres. Verified on
 * Espace Saint Quentin against 64 OpenStreetMap shop pins: the fitted reference lands 2.8 m from the vendor's.
 * `labels` is the centre's own shop-id-to-tenant list; a shop without a label is empty on the centre's site.
 */
export function visioglobeVenue(map: unknown, labels: Record<string, string>, source: string, updatedAt: string | null, directory: PropertyVenue['directory'] = null, conventions: VisioglobeConventions = ALTAREA_CONVENTIONS): PropertyVenue {
  const root = record(map), geo = record(root.geoinformation), pois = record(root.pois), routing = record(root.routing);
  const height = finite(root.svgHeight), scale = finite(geo.meters_per_pixel), rotation = finite(geo.rotation_angle_in_degrees);
  const xref = finite(geo.geo_xref), yref = finite(geo.geo_yref), pxref = finite(geo.pixel_xref) ?? 0, pyref = finite(geo.pixel_yref) ?? 0;
  const zone = Number(/--zone (\d+)/.exec(String(geo.proj ?? ''))?.[1]);
  if (height === null || scale === null || rotation === null || xref === null || yref === null || !zone || !/--north/.test(String(geo.proj))) throw new Error('Venue map has no usable northern UTM georeference.');
  const theta = rotation * Math.PI / 180, cos = Math.cos(theta), sin = Math.sin(theta);
  const toLngLat = (x: number, y: number) => {
    const dx = x - pxref, dy = height - y - pyref;
    return utmToLngLat(xref + scale * (cos * dx - sin * dy), yref + scale * (sin * dx + cos * dy), zone);
  };
  const floorOf = new Map(list(pois.places).map(place => [String(place.poiID), String(place.floor)]));
  // Map icons and routing points are not places; their floor is on the generic poi list, where 'outside' is no level.
  const poiFloor = new Map(conventions.readsPoiFloors ? list(pois.pois).flatMap(poi => typeof poi.floorName === 'string' ? [[String(poi.poiID), poi.floorName]] : []) : []);
  const levelOf = (floor: string | null | undefined) => floor === 'outside' ? null : floor ?? null;
  const layout = record(record(record(record(record(root.extra_data).config).venue_layout).buildings).B1);
  const names = record(record(record(record(record(record(record(root.extra_data).resources).en).localized).locale).en).venueLayout);
  const levels = Object.entries(record(layout.floors)).map(([id, floor]) => ({ id, name: String(record(names[id]).shortName ?? finite(record(floor).levelIndex) ?? id), ordinal: finite(record(floor).levelIndex) ?? 0 }))
    .sort((a, b) => a.ordinal - b.ordinal);

  const byId = new Map<string, PulseVenueFeature>();
  for (const polygon of list(pois.polygons)) {
    const id = String(polygon.poiID ?? ''), kind = conventions.kindOf(id);
    if (!kind || typeof polygon.points !== 'string') continue;
    const pixels = polygon.points.split(',').map(pair => pair.trim().split(/\s+/).map(Number)).filter(p => p.length === 2 && p.every(Number.isFinite)) as Array<[number, number]>;
    if (pixels.length < 3) continue;
    const area = Math.abs(pixels.reduce((sum, [x, y], i) => { const [nx, ny] = pixels[(i + 1) % pixels.length]!; return sum + x * ny - nx * y; }, 0)) / 2 * scale * scale;
    const ring = pixels.map(([x, y]) => toLngLat(x, y));
    const existing = byId.get(id);
    if (existing) { existing.rings.push(ring); existing.areaSqm += area; continue; }
    const floor = floorOf.get(id) ?? null;
    // Buildings repeat one id per footprint; only shops and amenities merge their pieces into one object.
    const key = kind === 'building' ? `${id}#${byId.size}` : id;
    byId.set(key, { id, kind, level: floor === 'outside' ? null : floor, name: kind === 'unit' ? labels[id] ?? null : kind === 'amenity' ? id.replace(/^S\d+_/, '').replace(/\d+$/, '') : null, areaSqm: area, rings: [ring] });
  }
  const features = [...byId.values()].map(f => ({ ...f, areaSqm: Math.round(f.areaSqm) })).filter(f => f.kind !== 'unit' || f.areaSqm >= (conventions.minUnitSqm ?? 0));

  // The walking network: nodes carry a floor index into routing.floors; shops and services hang off it by id.
  const floorNames = list(routing.floors).map(f => String(f.name));
  const nodes = list(routing.nodes);
  const network: WalkingNetwork = { nodes: nodes.length, edges: list(routing.edges).flatMap(e => finite(e.s) !== null && finite(e.e) !== null ? [[e.s as number, e.e as number, finite(e.l) ?? 1] as [number, number, number]] : []) };
  const routingNodes = new Map<string, number[]>();
  for (const entry of list(routing.poiRoutingNodes)) {
    const id = String(entry.a ?? ''), node = finite(entry.n);
    if (node === null) continue;
    routingNodes.set(id, [...(routingNodes.get(id) ?? []), node]);
  }
  const anchors: PulseVenueAnchor[] = [];
  const anchorNodes: number[][] = [];
  const nearest = (x: number, y: number, floor: string) => nodes.reduce<{ i: number; d: number }>((best, n, i) => {
    if (floorNames[finite(n.f) ?? -1] !== floor) return best;
    const d = Math.hypot((finite(n.x) ?? 0) - x, (finite(n.y) ?? 0) - y);
    return d < best.d ? { i, d } : best;
  }, { i: -1, d: Infinity }).i;
  for (const image of list(pois.images)) {
    const anchor = conventions.imageAnchor(image, id => floorOf.get(id) ?? poiFloor.get(id) ?? null);
    if (!anchor) continue;
    const node = nearest(finite(image.centerX) ?? 0, finite(image.centerY) ?? 0, anchor.floor);
    if (node >= 0) { anchors.push({ id: String(image.poiID), kind: anchor.kind, level: levelOf(anchor.floor) }); anchorNodes.push([node]); }
  }
  for (const [id, nodeList] of routingNodes) {
    const kind = conventions.routingAnchor(id);
    if (!kind) continue;
    anchors.push({ id, kind, level: floorOf.get(id) ?? levelOf(poiFloor.get(id)) });
    anchorNodes.push(nodeList);
  }
  const doors = new Map(features.filter(f => f.kind === 'unit' && routingNodes.has(f.id)).map(f => [f.id, routingNodes.get(f.id)!]));
  const { exposure, edgeWalks } = anchorNodes.length ? walkingLoads(network, anchorNodes, doors) : { exposure: new Map<string, PulseExposure>(), edgeWalks: [] };
  const round = (n: number) => Math.round(n * 1e7) / 1e7;
  const flows = edgeWalks.length ? corridorFlows(network, edgeWalks, node => {
    const n = nodes[node], level = floorNames[finite(n?.f) ?? -1];
    return n && level && level !== 'outside' ? { level, at: toLngLat(finite(n.x) ?? 0, finite(n.y) ?? 0).map(round) as [number, number] } : null;
  }) : [];
  return {
    source, updatedAt, directory, levels: levels.filter(level => features.some(f => f.level === level.id && f.kind === 'unit')),
    features: features.map(f => f.kind === 'unit' ? { ...f, exposure: exposure.get(f.id) ?? null } : f),
    anchors, missingAnchors: (['entrance', 'station', 'bus', 'parking'] as const).filter(kind => !anchors.some(a => a.kind === kind)), flows,
  };
}

const ringOf = (value: unknown): Array<[number, number]> | null => Array.isArray(value) && value.length >= 3 && value.every(p => Array.isArray(p) && p.length === 2 && p.every(n => typeof n === 'number' && Number.isFinite(n))) ? value as Array<[number, number]> : null;

export const pointInRing = ([x, y]: [number, number], ring: Array<[number, number]>) => ring.reduce((odd, [xi, yi], i) => {
  const [xj, yj] = ring[(i + ring.length - 1) % ring.length]!;
  return (yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi ? !odd : odd;
}, false);
/**
 * Visioglobe ships the whole site parcel (car parks and access roads included) as one more "building" footprint. Drawn
 * as floor it blacks out the streets around the centre, so a footprint that encloses every shop is not a building.
 */
const isSiteOutline = (f: PulseVenueFeature, units: PulseVenueFeature[]) => f.kind === 'building' && units.length > 1
  && units.every(u => u.rings.every(r => r.every(p => f.rings.some(ring => pointInRing(p, ring)))));
const dropSiteOutline = (features: PulseVenueFeature[]) => {
  const units = features.filter(f => f.kind === 'unit');
  return features.filter(f => !isSiteOutline(f, units));
};

/**
 * Applies the landlord's merchandising plan over the venue read from the public website: per shop, its tenant (null =
 * vacant), its unit code, its leasing area, or that it is not a shop at all. Stored apart from the venue, so a fresh
 * import of the website never undoes it; occupancy is then sourced to the plan.
 */
export function applyVenuePlan(venue: PropertyVenue, value: unknown): PropertyVenue {
  const plan = record(value);
  const units = record(plan.units);
  if (typeof plan.source !== 'string' || typeof plan.date !== 'string' || !Object.keys(units).length) return venue;
  return {
    ...venue,
    directory: { source: plan.source, importedAt: plan.date },
    features: venue.features.map(f => {
      const fix = f.kind === 'unit' ? record(units[f.id]) : {};
      if (!Object.keys(fix).length) return f;
      if (fix.kind === 'amenity') return { ...f, kind: 'amenity' as const, name: null };
      return {
        ...f,
        ...('name' in fix && (fix.name === null || typeof fix.name === 'string') ? { name: fix.name as string | null } : {}),
        ...(typeof fix.code === 'string' ? { code: fix.code } : {}),
        ...(finite(fix.areaSqm) ? { areaSqm: fix.areaSqm as number } : {}),
      };
    }),
  };
}

/** Reads a stored venue back, keeping only well-formed features. */
export function storedVenue(value: unknown): PropertyVenue | null {
  const venue = record(value);
  if (!Array.isArray(venue.levels) || !Array.isArray(venue.features)) return null;
  const directory = record(venue.directory);
  return {
    source: String(venue.source ?? ''), updatedAt: typeof venue.updatedAt === 'string' ? venue.updatedAt : null,
    directory: typeof directory.source === 'string' && typeof directory.importedAt === 'string' ? { source: directory.source, importedAt: directory.importedAt } : null,
    levels: list(venue.levels).filter(l => typeof l.id === 'string').map(l => ({ id: l.id as string, name: String(l.name ?? l.id), ordinal: finite(l.ordinal) ?? 0 })),
    features: dropSiteOutline(list(venue.features).flatMap(f => {
      const rings = (Array.isArray(f.rings) ? f.rings : [f.ring]).map(ringOf).filter((r): r is Array<[number, number]> => !!r);
      if (typeof f.id !== 'string' || !['unit', 'amenity', 'building'].includes(f.kind as string) || !rings.length) return [];
      const exposure = record(f.exposure);
      return [{ id: f.id, kind: f.kind as PulseVenueFeature['kind'], level: typeof f.level === 'string' ? f.level : null, name: typeof f.name === 'string' ? f.name : null, areaSqm: finite(f.areaSqm) ?? 0, rings,
        ...(typeof f.brandId === 'string' && f.brandId ? { brandId: f.brandId } : {}),
        exposure: finite(exposure.routes) !== null && finite(exposure.percentile) !== null ? { routes: exposure.routes as number, percentile: exposure.percentile as number } : null }];
    })),
    anchors: list(venue.anchors).filter(a => typeof a.id === 'string' && ['entrance', 'station', 'bus', 'parking'].includes(a.kind as string)).map(a => ({ id: a.id as string, kind: a.kind as PulseVenueAnchor['kind'], level: typeof a.level === 'string' ? a.level : null })),
    missingAnchors: (Array.isArray(venue.missingAnchors) ? venue.missingAnchors : []).filter((k): k is PulseVenueAnchor['kind'] => ['entrance', 'station', 'bus', 'parking'].includes(k as string)),
    flows: list(venue.flows).flatMap(f => {
      const ok = Array.isArray(f.path) && f.path.length === 2 && f.path.every(p => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite));
      return ok && typeof f.level === 'string' && finite(f.walks) ? [{ level: f.level, path: f.path as PulseFlow['path'], walks: f.walks as number }] : [];
    }),
  };
}

/**
 * Links every named shop to its Brand record: the brand its importer stored after an exact match (loosely, "CARREFOUR"
 * would take the centre's tenant Carrefour Voyages), else first among the centre's own tenants (its units' active
 * leases, loosely, since that list is short and closed), then by exact name in the shared brand referential. A shop
 * that links to nothing keeps its label and is shown as a tenant our records do not hold.
 */
export function linkVenueBrands(venue: PropertyVenue, tenants: Array<{ id: string; name: string; status: UnitStatus | null }>, referential: Array<{ id: string; name: string }>): PropertyVenue {
  return { ...venue, features: venue.features.map(f => {
    if (f.kind !== 'unit' || !f.name) return f;
    const tenant = f.brandId ? (tenants.some(t => t.id === f.brandId) ? f.brandId : null) : linkBrand(f.name, tenants, true);
    const brandId = f.brandId ?? tenant ?? linkBrand(f.name, referential, false);
    return { ...f, brandId, unitStatus: tenant ? tenants.find(t => t.id === tenant)?.status ?? null : null };
  }) };
}
/** The shop labels the centre's tenants do not cover: the only names worth looking up in the shared referential. */
export const unlinkedLabels = (venue: PropertyVenue, tenants: Array<{ id: string; name: string }>) =>
  [...new Set(venue.features.flatMap(f => f.kind === 'unit' && f.name && !f.brandId && !linkBrand(f.name, tenants, true) ? [f.name.trim()] : []))];
